import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import IMAGES from '../images';
import './Navbar.css';

const NAV_LINKS = [
  { label: 'Accueil', path: '/' },
  { label: 'Nouvelle Collection', path: '/collection-heritage' },
  {
    label: 'Robinetterie',
    path: '/robinetterie',
    children: [
      { label: 'Douches', path: '/robinetterie/douches' },
      { label: 'Robinets Muraux', path: '/robinetterie/muraux' },
      { label: 'Robinets à Poser', path: '/robinetterie/poser' },
      { label: 'Robinet suspendu Leaf', path: '/robinetterie/leaf' },
    ],
  },
  {
    label: 'Vasques',
    path: '/vasques',
    children: [
      { label: 'Vasques en Marbre', path: '/vasques/marbre' },
      { label: 'Vasques en Laiton Martelé', path: '/vasques/laiton' },
      { label: 'Vasques en Céramique', path: '/vasques/ceramique' },
    ],
  },
  {
    label: 'Accessoires',
    path: '/accessoires',
    children: [
      { label: 'Porte-Savons', path: '/accessoires/porte-savons' },
      { label: 'Porte-Serviettes', path: '/accessoires/porte-serviettes' },
      { label: 'Portes Papier Toilette', path: '/accessoires/papier-toilette' },
      { label: 'Porte-Brosses', path: '/accessoires/porte-brosses' },
      { label: 'Miroirs', path: '/accessoires/miroirs' },
      { label: 'Bougeoirs', path: '/accessoires/bougeoirs' },
    ],
  },
  { label: 'Nos Créations Chez Vous', path: '/creations' },
  { label: 'Presse', path: '/presse' },
  { label: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <nav className={`navbar ${scrolled ? 'navbar--scrolled' : ''}`} ref={navRef}>
        <div className="navbar__inner">
          {/* Logo */}
          <Link to="/" className="navbar__logo">
            <img
              src={IMAGES.logo}
              alt="Khansa Yazidi"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
            <span className="navbar__logo-text" style={{ display: 'none' }}>
              KHANSA YAZIDI
            </span>
          </Link>

          {/* Desktop Links */}
          <ul className="navbar__links">
            {NAV_LINKS.map((link) => (
              <li
                key={link.path}
                className="navbar__item"
                onMouseEnter={() => link.children && setOpenDropdown(link.path)}
                onMouseLeave={() => setOpenDropdown(null)}
              >
                <Link
                  to={link.path}
                  className={`navbar__link ${location.pathname === link.path ? 'navbar__link--active' : ''}`}
                >
                  {link.label}
                  {link.children && <span className="navbar__caret">▾</span>}
                </Link>
                {link.children && openDropdown === link.path && (
                  <div className="navbar__dropdown">
                    {link.children.map((child) => (
                      <Link key={child.path} to={child.path} className="navbar__dropdown-link">
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>

          {/* CTA */}
          <div className="navbar__cta">
            <a href="mailto:yazidi.khansa@gmail.com?subject=Demande%20de%20Devis" className="navbar__cta-btn">
              Demander un Devis
            </a>
          </div>

          {/* Hamburger */}
          <button
            className={`navbar__hamburger ${mobileOpen ? 'navbar__hamburger--open' : ''}`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <div className={`mobile-menu ${mobileOpen ? 'mobile-menu--open' : ''}`}>
        <div className="mobile-menu__inner">
          {NAV_LINKS.map((link) => (
            <div key={link.path} className="mobile-menu__item">
              {link.children ? (
                <>
                  <button
                    className="mobile-menu__link mobile-menu__toggle"
                    onClick={() =>
                      setMobileExpanded(mobileExpanded === link.path ? null : link.path)
                    }
                  >
                    {link.label}
                    <span className={`mobile-menu__caret ${mobileExpanded === link.path ? 'open' : ''}`}>▾</span>
                  </button>
                  {mobileExpanded === link.path && (
                    <div className="mobile-menu__sub">
                      {link.children.map((child) => (
                        <Link key={child.path} to={child.path} className="mobile-menu__sub-link">
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link to={link.path} className="mobile-menu__link">
                  {link.label}
                </Link>
              )}
            </div>
          ))}
          <div className="mobile-menu__cta">
            <a href="mailto:yazidi.khansa@gmail.com?subject=Demande%20de%20Devis" className="btn-outline">
              Demander un Devis
            </a>
          </div>
        </div>
      </div>
      {mobileOpen && (
        <div className="mobile-menu__overlay" onClick={() => setMobileOpen(false)} />
      )}
    </>
  );
}
